/**
 * useSubmitAnswer Hook
 *
 * Submits an answer to an exam question using URQL mutation.
 */

import { useMutation } from "urql";
import type { ExamQuestionView, ExamAttempt } from "./useCourse";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const AnswerExamQuestionDocument = `
  mutation AnswerExamQuestion($attemptId: ID!, $questionId: ID!, $answer: String!) {
    answerExamQuestion(attemptId: $attemptId, questionId: $questionId, answer: $answer) {
      correct
      nextQuestion {
        id
        questionNumber
        totalQuestions
        text
        options {
          id
          text
        }
      }
      completed
      attempt {
        id
        score
        maxScore
        percentage
        passed
        totalWrongAttempts
        startedAt
        completedAt
      }
      wrongAttemptCount
      courseUrl
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface AnswerResult {
  correct: boolean;
  nextQuestion: ExamQuestionView | null;
  completed: boolean;
  attempt: ExamAttempt;
  wrongAttemptCount: number;
  courseUrl: string | null;
}

interface SubmitAnswerInput {
  attemptId: string;
  questionId: string;
  answer: string;
}

interface UseSubmitAnswerResult {
  submitAnswer: (input: SubmitAnswerInput) => Promise<AnswerResult>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Submit an answer to an exam question.
 */
export function useSubmitAnswer(): UseSubmitAnswerResult {
  const [result, executeMutation] = useMutation<{
    answerExamQuestion: AnswerResult;
  }>(AnswerExamQuestionDocument);

  const submitAnswer = async (input: SubmitAnswerInput): Promise<AnswerResult> => {
    const response = await executeMutation(input);
    if (response.error) {
      throw response.error;
    }
    if (!response.data?.answerExamQuestion) {
      throw new Error("Failed to submit answer");
    }
    return response.data.answerExamQuestion;
  };

  return {
    submitAnswer,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
